import { Label } from 'reactstrap';
import TrackLocationMapFilter from '../../filters/TrackLocationFilter';
import React, { useEffect, useState } from 'react';
import '../../assets/style/TrackLocation.css';
import {
  LastLocationType,
  LoggedUserType,
  TLStatusCountType,
  TrackingDateType,
  TrackingHistoryType,
  TrackLocationFilterType,
  UserType,
} from '../../utils/Types';
import { AdvancedMarker, Map, useMap } from '@vis.gl/react-google-maps';
import { Image } from 'react-bootstrap';
import {
  clearTrackHistoryList,
  getLastLocationList,
  trackLocationSelector,
} from '../../features/trackLocationSlice';

import MarkerIcon from '../../assets/img/marker_icon.svg';
import ProfileMarker from '../../components/ProfileMarker';
import MarkerToolTip from '../../components/MarkerToolTip';
import TrackLocationHistory from './TrackLocationHistory';
import { GOOGLE_MAP_ID } from '../../utils/Config';
import { useLocation } from 'react-router-dom';
import CenterSpinner from '../../components/CenterSpinner';
import { useAppDispatch, useAppSelector } from '../../store/redux-hooks';
import { loginSelector } from '../../features/loginSlice';
import ToggleSwitch from '../../components/ToggleSwitch';
import {
  getFindLocationRequest,
  getLineAndToolTip,
  makeZoomCenter,
  onDataChange,
  onUserMarkerClick,
  showTollTip,
} from './TrackLocationHelper';
import { useLoader } from '../../loader/LoaderProvider';
import { gatDistance, getFormattedTime } from '../../utils/Util';
import moment from 'moment';

export type Polyline = google.maps.Polyline;
const positionDefault = { lat: 17.38405, lng: 78.45636 };

const TrackLocationMap: React.FunctionComponent = () => {
  const mapInstance = useMap();
  const [trackLocationFilter, setTrackLocationFilter] =
    useState<TrackLocationFilterType>();
  const { showLoader, setShowLoader } = useLoader();
  const [polylineLatLngList, setPolylineLatLngList] = useState<any[]>([]);
  const [selectedUserLastLocation, setSelectedUserLastLocation] =
    useState<LastLocationType | null>();
  const [toolTipStatus, setToolTipStatus] = useState<boolean[] | null>();
  const [trackingDate, setTrackingDate] = useState<TrackingDateType>();
  const location = useLocation();
  const [refreshKey, setRefreshKey] = useState(0);
  const locationListError: string | null = useAppSelector(
    trackLocationSelector
  ).locationListError;
  const employeeLastLocationList: LastLocationType[] = useAppSelector(
    trackLocationSelector
  ).lastLocationList;
  const trackHistoryList: TrackingHistoryType[] = useAppSelector(
    trackLocationSelector
  ).trackHistoryList;
  const statusCount: TLStatusCountType = useAppSelector(
    trackLocationSelector
  ).statusCount;
  const user: LoggedUserType = useAppSelector(loginSelector).user;
  const userDetails: UserType = useAppSelector(loginSelector).userDetails;
  const [showFilter, setShowFilter] = useState<boolean>(true);
  const [directionsRenderers, setDirectionsRenderers] = useState<
    google.maps.DirectionsRenderer[]
  >([]);

  const dispatch = useAppDispatch();

  useEffect(() => {
    console.log('track location refreshed');
    const interval = setInterval(
      () => {
        const currentTime = new Date().toLocaleString();
        console.log(`Location list refreshed at: ${currentTime}`);

        setRefreshKey((prev) => prev + 1); // Triggers re-render or data fetch
        findLocationList(trackLocationFilter); // Re-fetch location data
      },
      20 * 60 * 1000
    ); // 20 minutes
    return () => clearInterval(interval); // Cleanup on unmount
  }, [trackLocationFilter]);

  const clearData = () => {
    dispatch(clearTrackHistoryList());
    setPolylineLatLngList([]);
    setToolTipStatus(null);
    directionsRenderers.forEach((renderer) => {
      renderer.setMap(null);
    });
    setDirectionsRenderers([]);
  };
  const findLocationList = (trackLocationFilter?: TrackLocationFilterType) => {
    clearData();
    setSelectedUserLastLocation(null);
    setShowLoader(true);
    dispatch(
      getLastLocationList(
        getFindLocationRequest(
          setTrackingDate,
          user,
          userDetails,
          trackLocationFilter
        )
      )
    );
  };
  // **Make API request to Google Directions API**
  const getRoute = (
    directionsService: google.maps.DirectionsService,
    request: google.maps.DirectionsRequest
  ) => {
    return new Promise<google.maps.DirectionsResult | null>((resolve) => {
      directionsService.route(request, (result, status) => {
        if (status === 'OK' && result) {
          resolve(
            result.routes.length > 0
              ? { ...result, routes: [result.routes[0]] }
              : null
          );
        } else {
          console.error('Directions request failed:', status);
          resolve(null);
        }
      });
    });
  };
  // **Break waypoints into multiple requests**
  const getSegmentedRoutes = async (line: any[], directionsService: any) => {
    const routes = [];
    const MAX_WAYPOINTS = 23; // Google API allows max 23 waypoints per request
    for (let i = 0; i < line.length - 1; i += MAX_WAYPOINTS) {
      const start = i;
      const end = Math.min(i + MAX_WAYPOINTS, line.length - 1);
      const waypoints = line.slice(start + 1, end).map((point) => ({
        location: new google.maps.LatLng(point.lat, point.lng),
        stopover: true, // Enforce exact travel points
      }));
      const request = {
        origin: new google.maps.LatLng(line[start].lat, line[start].lng),
        destination: new google.maps.LatLng(line[end].lat, line[end].lng),
        waypoints,
        travelMode: google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false,
        provideRouteAlternatives: false,
        avoidHighways: false,
        avoidTolls: false,
      };
      const result = await getRoute(directionsService, request);
      if (result) {
        routes.push(result);
      }
    }
    return routes;
  };
  /**
   * Sort the trackHistoryList on the basis of time and find the distance between two lat and lng.
   */
  const onTrackingHistoryReceived = async () => {
    setShowLoader(false);
    if (!trackHistoryList || trackHistoryList.length === 0) return;
    const { line, toolTip } = getLineAndToolTip(trackHistoryList);

    if (mapInstance) {
      mapInstance.setCenter(line[0]);
      if (mapInstance.getZoom()) {
        mapInstance.setZoom(20);
      }
    }

    setToolTipStatus(toolTip);
    setPolylineLatLngList(line);
    if (line.length === 0) return;
    const directionsService = new google.maps.DirectionsService();
    setDirectionsRenderers((prevRenderers) => {
      prevRenderers.forEach((renderer) => renderer.setMap(null));
      return [];
    });
    const routes = await getSegmentedRoutes(line, directionsService);
    const newRenderers: google.maps.DirectionsRenderer[] = [];
    routes.forEach((result) => {
      if (result) {
        const segmentRenderer = new google.maps.DirectionsRenderer({
          suppressMarkers: true,
          preserveViewport: true,
          polylineOptions: {
            strokeColor: '#FF0000',
            strokeOpacity: 0.8,
            strokeWeight: 5,
          },
        });
        segmentRenderer.setMap(mapInstance);
        segmentRenderer.setDirections(result);
        newRenderers.push(segmentRenderer);
      }
    });
    setDirectionsRenderers(newRenderers);
  };
  const onProfileMarkerClick = (
    lastLocation: LastLocationType,
    selectedDate?: string
  ) => {
    setSelectedUserLastLocation(lastLocation);
    setTrackingDate({
      date: lastLocation.locationreceivedat,
      activeDateNumber: 1,
    });
    onUserMarkerClick(
      clearData,
      dispatch,
      setShowLoader,
      lastLocation.employeeid,
      lastLocation.locationreceivedat
        ? lastLocation.locationreceivedat
        : selectedDate,
      trackingDate
    );
  };

  useEffect(() => {
    setShowLoader(false);
    onTrackingHistoryReceived();
  }, [trackHistoryList]);
  useEffect(() => {
    setShowLoader(false);
  }, [employeeLastLocationList]);
  useEffect(() => {
    if (mapInstance) {
      makeZoomCenter(mapInstance, employeeLastLocationList, locationListError);
    }
  }, [employeeLastLocationList, mapInstance]);

  useEffect(() => {}, [mapInstance]);
  useEffect(() => {
    onDataChange(
      userDetails,
      mapInstance,
      location,
      setTrackingDate,
      onProfileMarkerClick,
      findLocationList,
      trackLocationFilter
    );
  }, [location, mapInstance, user, userDetails]);

  const handleToggleSwitch = () => {
    setShowFilter((prev) => !prev);
  };
  const getDistanceTravelledAndTime = (index: number) => {
    try {
      const currentItem = trackHistoryList[index];
      if (!currentItem) return '- | -';
      const isFirstItem = index === 0;
      const isTask = currentItem.referenceModuleName === 'Task';
      const isLogged = currentItem.referenceModuleName === 'Logged';
      if (!isFirstItem && !isTask) {
        return '- | -';
      }

      let nextTaskItem = null;
      let firstLoggedItem = null;

      for (let i = index + 1; i < trackHistoryList.length; i++) {
        const item = trackHistoryList[i];

        if (item.referenceModuleName === 'Task') {
          nextTaskItem = item;
          break; // Found next Task
        }

        if (
          !firstLoggedItem &&
          item.referenceModuleName === 'Logged' &&
          i === trackHistoryList.length - 1
        ) {
          // Only pick last Logged item
          firstLoggedItem = item;
        }
      }

      let referenceItem = null;

      if (nextTaskItem) {
        referenceItem = nextTaskItem;
      } else if (firstLoggedItem) {
        referenceItem = firstLoggedItem;
      }

      if (!referenceItem) {
        return '- | -';
      }

      const distance = gatDistance(
        Number(currentItem.latitude),
        Number(currentItem.longitude),
        Number(referenceItem.latitude),
        Number(referenceItem.longitude)
      );

      const currentTime = moment(currentItem.locationReceivedAt);
      const referenceTime = moment(referenceItem.locationReceivedAt);
      const minutesDiff = Math.abs(currentTime.diff(referenceTime, 'minutes'));

      const formattedTime = getFormattedTime(minutesDiff);

      return `${distance} | ${formattedTime}`;
    } catch (error) {
      console.log('Error calculating distance and time:', error);
      return '- | -';
    }
  };
  return (
    <div className="ml-4 size-100">
      <Label className="text-heading">Track Location</Label>
      <ToggleSwitch isOpen={showFilter} onClick={handleToggleSwitch} />
      <div className="task-item-parent-view">
        {showFilter && (
          <TrackLocationMapFilter
            onApplyFilters={(trackLocationFilter: TrackLocationFilterType) => {
              setTrackLocationFilter(trackLocationFilter);
              findLocationList(trackLocationFilter);
            }}
          />
        )}
        {!selectedUserLastLocation && (
          <div className="d-flex justify-content-end">
            <Label className="mt-2 active-emp">
              {!trackLocationFilter ||
              trackLocationFilter?.status?.value === 'Logged'
                ? 'Logged Employees'
                : trackLocationFilter?.status?.value === 'Not-Logged'
                  ? 'Not-Logged Employees'
                  : 'Logged Employees'}
              : {statusCount?.userCount} /
            </Label>
            <Label className="mt-2 active-total">
              {statusCount.totalUsers}
            </Label>
          </div>
        )}
        <div
          className={`${!selectedUserLastLocation ? 'mt-1' : 'mt-3'} pb-4 size-map`}
        >
          <Map
            key={refreshKey}
            defaultCenter={positionDefault}
            defaultZoom={11}
            gestureHandling="greedy"
            mapId={GOOGLE_MAP_ID}
          >
            {!selectedUserLastLocation &&
              employeeLastLocationList?.length > 0 &&
              employeeLastLocationList?.map(
                (lastLocation: LastLocationType) => (
                  <div key={lastLocation.candidateuniqueid}>
                    {lastLocation.latitude && lastLocation.longitude && (
                      <ProfileMarker
                        photo={lastLocation.photo}
                        lastLocation={lastLocation}
                        name={lastLocation.employeename}
                        roleName={lastLocation.rolename}
                        onUserMarkerClick={(lastLocation: LastLocationType) => {
                          onProfileMarkerClick(lastLocation);
                        }}
                        enableHover={true}
                      />
                    )}
                  </div>
                )
              )}

            {selectedUserLastLocation &&
              polylineLatLngList &&
              polylineLatLngList.length > 1 && (
                <AdvancedMarker position={polylineLatLngList[0]}>
                  <Image src={MarkerIcon} />
                </AdvancedMarker>
              )}

            {selectedUserLastLocation &&
              polylineLatLngList &&
              polylineLatLngList.length > 1 && (
                <AdvancedMarker
                  position={polylineLatLngList[polylineLatLngList.length - 1]}
                >
                  <Image src={MarkerIcon} />
                </AdvancedMarker>
              )}
            {selectedUserLastLocation &&
              trackHistoryList &&
              trackHistoryList.length > 0 && (
                <ProfileMarker
                  photo={
                    selectedUserLastLocation?.photo
                      ? selectedUserLastLocation?.photo
                      : ''
                  }
                  name={selectedUserLastLocation.employeename}
                  roleName={selectedUserLastLocation.rolename}
                  lastLocation={
                    {
                      ...trackHistoryList[0],
                      status: selectedUserLastLocation.status,
                    } as any
                  }
                  enableHover={false}
                />
              )}
            {selectedUserLastLocation &&
              polylineLatLngList &&
              polylineLatLngList.length > 0 &&
              polylineLatLngList
                .slice()
                .reverse()
                .map((item, index) => {
                  // Ensure we properly format latitude/longitude
                  const isTaskModule =
                    trackHistoryList[index]?.referenceModuleName === 'Task';

                  const markerPosition = isTaskModule
                    ? new google.maps.LatLng(
                        parseFloat(trackHistoryList[index]?.latitude),
                        parseFloat(trackHistoryList[index]?.longitude)
                      )
                    : new google.maps.LatLng(
                        parseFloat(item.lat),
                        parseFloat(item.lng)
                      );

                  return (
                    <AdvancedMarker
                      key={`${markerPosition.lat()},${markerPosition.lng()}`} // Ensure correct key format
                      position={markerPosition} // Pass LatLng instance
                      onClick={() => {}}
                    >
                      <MarkerToolTip
                        data={trackHistoryList[index]}
                        address={trackHistoryList?.[index]?.locationName ?? ''}
                        showToolTip={
                          toolTipStatus ? toolTipStatus[index] : false
                        }
                        setShowToolTip={(show: boolean) => {
                          const array = toolTipStatus?.map((value, i) =>
                            i === index ? show : value
                          );
                          setToolTipStatus(array);
                        }}
                        distanceTravelledAndTime={getDistanceTravelledAndTime(
                          index
                        )}
                        indexNumber={index}
                      />
                    </AdvancedMarker>
                  );
                })}
          </Map>
        </div>
        {selectedUserLastLocation && (
          <TrackLocationHistory
            selectedDate={trackingDate?.date}
            activeDateNumber={trackingDate?.activeDateNumber}
            trackHistoryList={polylineLatLngList}
            showTollTip={(index: number) => {
              showTollTip(setToolTipStatus, index, toolTipStatus);
            }}
            lastLocation={selectedUserLastLocation}
            onTrackingDateChange={(date: string, activeDateNumber: number) => {
              setTrackingDate((prev: any) => ({
                ...prev,
                activeDateNumber: activeDateNumber,
              }));
              onUserMarkerClick(
                clearData,
                dispatch,
                setShowLoader,
                selectedUserLastLocation?.employeeid,
                date,
                trackingDate
              );
            }}
          />
        )}
      </div>
      <CenterSpinner showLoader={showLoader} className="spinner-60" />
    </div>
  );
};

export default TrackLocationMap;
